import { Icon, SelectMenu, Tabs, Typography, Checkbox } from '@scale/scaleui';
import { InteractionStepType } from '@scale/shared/types/chat';
import { memo, useMemo, useState } from 'react';
import { TOOL_USE_PROCESS_SUPERVISION_CONFIG_OPTIONS } from '@scale/shared/types/chat/toolUseProcessSupervision';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';
import { ZodFormBuilder } from 'react-scale/corp/views/CorpProjectGroupManager/views/Linter/GenericLinterView/ZodFormBuilder';
import { z } from 'zod';
import { StepConfigurationComponentProps } from '../../stepDescriptor/types';
import { REVIEW_LEVELS } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/configs/common';
import { ReviewLevel } from '@scale/shared-types';

const tabOptions = [
  { id: 'GENERAL' as const, label: 'General' },
  { id: 'TOOLS' as const, label: 'Tools' },
];
type TabOption = (typeof tabOptions)[number];

const ACTION_PLAN_OPTIONS = [
  { id: 'YES', name: 'Yes', value: true },
  { id: 'NO', name: 'No', value: false },
];

const ToolIdsSchema = z.array(z.string()).optional();

function ToolUseProcessSupervisionConfigurationComponent({
  params: currentParams,
  setParams: setCurrentParams,
}: StepConfigurationComponentProps<InteractionStepType.ToolUseProcessSupervision>) {
  const [tab, setTab] = useState<TabOption['id']>('GENERAL');
  const [readOnly, setReadOnly] = useState(false);
  const { actionPlanId } = useMemo(() => {
    const actionPlanOption = ACTION_PLAN_OPTIONS.find(
      option => option.value === !!currentParams.params.isActionPlanEnabled,
    );
    if (!actionPlanOption) {
      throw new Error('Action plan option not found');
    }
    return { actionPlanId: actionPlanOption.id };
  }, [currentParams]);

  const ToggleReadOnlyReviewLevel = (level: ReviewLevel) => {
    const reviewLevels = currentParams.params.run_tool_only_review_levels || [];
    if (reviewLevels.includes(level)) {
      // remove the level
      setCurrentParams({
        ...currentParams,
        params: {
          ...currentParams.params,
          run_tool_only_review_levels: reviewLevels.filter(l => l !== level),
        },
      });
    } else {
      // add the level
      setCurrentParams({
        ...currentParams,
        params: {
          ...currentParams.params,
          run_tool_only_review_levels: [...reviewLevels, level],
        },
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Tabs
        tabs={tabOptions}
        value={tab}
        onChange={tabId => setTab(tabId as TabOption['id'])}
        variant="default"
      />

      {tab === 'GENERAL' && (
        <>
          <SelectMenu
            label={`Project`}
            options={TOOL_USE_PROCESS_SUPERVISION_CONFIG_OPTIONS}
            selectedOptionId={currentParams.params.configId}
            setSelected={configId =>
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, configId },
              })
            }
            required
          />
          <SelectMenu
            label={`Show action plan`}
            options={ACTION_PLAN_OPTIONS}
            selectedOptionId={actionPlanId}
            setSelected={selectedActionPlanId => {
              const actionPlanOption = ACTION_PLAN_OPTIONS.find(
                option => option.id === selectedActionPlanId,
              );
              if (!actionPlanOption) {
                throw new Error('Action plan option not found');
              }
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, isActionPlanEnabled: actionPlanOption.value },
              });
            }}
            required
          />
          <BooleanInputBuilder
            name="Enable contributor to manually mark prompts as complete"
            existingValue={!!currentParams.params.enable_manual_mark_as_complete}
            updateProperty={value => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, enable_manual_mark_as_complete: value },
              });
            }}
          />
          <BooleanInputBuilder
            name="Require at least one incorrect rating across all turns."
            existingValue={!!currentParams.params.require_incorrect_rating}
            updateProperty={value => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, require_incorrect_rating: value },
              });
            }}
          />
          <BooleanInputBuilder
            name="For python interpreter tool only, concatenate code from previous steps"
            existingValue={!!currentParams.params.concatenateCodeFromPreviousSteps}
            updateProperty={value => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, concatenateCodeFromPreviousSteps: value },
              });
            }}
          />
          <BooleanInputBuilder
            name="Skip observation"
            existingValue={!!currentParams.params.skip_observation}
            updateProperty={value => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, skip_observation: value },
              });
            }}
          />

          <BooleanInputBuilder
            name="Allow adding lone actions or lone thoughts"
            existingValue={!!currentParams.params.allow_add_actions}
            updateProperty={value => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, allow_add_actions: value },
              });
            }}
          />

          <BooleanInputBuilder
            name="Allow adding thought + action pairs"
            existingValue={!!currentParams.params.allow_add_thought_and_action}
            updateProperty={value => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, allow_add_thought_and_action: value },
              });
            }}
          />

          <BooleanInputBuilder
            name="Allow deleting actions"
            existingValue={!!currentParams.params.allow_delete_actions}
            updateProperty={value => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, allow_delete_actions: value },
              });
            }}
          />
          <BooleanInputBuilder
            name="Allow Tool Call Response Errors"
            existingValue={!!currentParams.params.allow_tool_call_response_errors}
            updateProperty={value => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, allow_tool_call_response_errors: value },
              });
            }}
          />
          <BooleanInputBuilder
            name="Allow Tool Call Response To Be Edited"
            existingValue={!!currentParams.params.can_edit_observation}
            updateProperty={value => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, can_edit_observation: value },
              });
            }}
          />
          <BooleanInputBuilder
            name="Make Run Tool Only"
            hint="If checked, the contributor will only be allowed to click the 'run tool' button. They will not be able to edit the tool call or observation."
            existingValue={readOnly}
            updateProperty={value => {
              setReadOnly(value);
            }}
          />
          {readOnly && (
            <div className="mb-4">
              <div className="prose text-sm italic mb-3">
                Choose which review levels are not allowed to edit the tool call or observation.
              </div>
              <div className="flex flex-col">
                {REVIEW_LEVELS.map(opts => (
                  <Checkbox
                    key={'visible-level-' + opts.level}
                    id={opts.level.toString()}
                    label={opts.name}
                    checked={currentParams.params.run_tool_only_review_levels?.includes(opts.level)}
                    onChange={() => ToggleReadOnlyReviewLevel(opts.level)}
                  />
                ))}
              </div>
            </div>
          )}

          <BooleanInputBuilder
            name="Only allow running the tool if there is any incorrect rating"
            existingValue={!!currentParams.params.run_tool_only_incorrect_rating}
            updateProperty={value => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, run_tool_only_incorrect_rating: value },
              });
            }}
          />
        </>
      )}

      {tab === 'TOOLS' && (
        <div>
          <Typography.H3>Tool Configuration</Typography.H3>
          <Typography.Paragraph2 className="mb-4">
            Configure the list of tool IDs that can be used in this process supervision step.
          </Typography.Paragraph2>
          {/* TODO(@dannyadkins @averyyip): update this to direclty pull from the new shared types */}
          <ZodFormBuilder
            label={'Tool IDs'}
            schema={ToolIdsSchema}
            value={currentParams.params.tool_ids || []}
            onChange={(value: string[]) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, tool_ids: value },
              });
            }}
          />
          <br />
          <ZodFormBuilder
            label={
              'Or restrict to a Subset of Tool IDs via Template Variable. Make sure to use brackets i.e. {{tools}}. This field will be used if specified'
            }
            schema={z.string().optional()}
            value={currentParams.params.tool_ids_via_template_variable}
            onChange={(value: string[]) => {
              setCurrentParams({
                ...currentParams,
                params: {
                  ...currentParams.params,
                  tool_ids_via_template_variable: value,
                },
              });
            }}
          />
          <br />
          <BooleanInputBuilder
            name="Do not include finish tool by default"
            hint="By default, the finish tool is always available for the user to select. Check this box if you want to only include it when specified by the taxonomy."
            existingValue={!!currentParams.params.do_not_include_finish_by_default}
            updateProperty={value => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, do_not_include_finish_by_default: value },
              });
            }}
          />
          <br />
          <ZodFormBuilder
            label={'Minimum number of unique tools required'}
            schema={z.string().optional()}
            value={currentParams.params.min_num_tools_used}
            onChange={(value: string[]) => {
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, min_num_tools_used: value },
              });
            }}
          />
          <br />
          <div className="w-full bg-white border border-neutral-200 shadow-sm rounded-lg py-4 px-3 relative">
            <Typography.Paragraph2 className="mb-4 flex items-center">
              <Icon className="mr-4" variant="warning" icon="brake-warning" size="lg" />
              Warning: if a model name is provided, the tool API responses will be generated via the
              chat model. The data should be not used for model training without the consent from
              the customer
            </Typography.Paragraph2>
            <ZodFormBuilder
              label={'Model Name'}
              schema={z.string().optional()}
              value={currentParams.params.modelName}
              onChange={(value: string[]) => {
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, modelName: value },
                });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(ToolUseProcessSupervisionConfigurationComponent);
